import { Snowflake, Wifi, PlugZap, Armchair, Tv, Luggage, Bath, GlassWater, Clock, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { money, fmtTime, addMinutes, durationText } from "@/lib/format";

const ICONS = { AC: Snowflake, WiFi: Wifi, "USB Charging": PlugZap, "Reclining Seats": Armchair, TV: Tv, "Luggage Hold": Luggage, Toilet: Bath, Water: GlassWater };

export const Amenities = ({ list = [] }) => (
  <div className="flex flex-wrap gap-1.5">
    {list.map((a) => {
      const I = ICONS[a] || Armchair;
      return <span key={a} className="chip bg-sky-50 text-sky-700"><I className="h-3 w-3" />{a}</span>;
    })}
  </div>
);

export const ViaLine = ({ trip }) => {
  const partial = trip.route_origin_city && (trip.route_origin_city !== trip.origin_city || trip.route_destination_city !== trip.destination_city);
  if (!trip.via?.length && !partial) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500" data-testid={`trip-via-${trip.id}`}>
      {trip.via?.length > 0 && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-[#0052FF]" />Stops at {trip.via.join(", ")}</span>}
      {partial && <span className="chip bg-amber-50 text-amber-700">Bus route {trip.route_origin_city} → {trip.route_destination_city}</span>}
    </div>
  );
};

export default function TripCard({ trip, onSelect, compact = false, active = false }) {
  const low = trip.seats_available <= 5;
  return (
    <div className={`card-soft rise group p-4 transition-[box-shadow,border-color] hover:border-blue-300 ${compact ? "" : "sm:p-5"} ${active ? "border-[#0052FF] ring-2 ring-blue-100" : ""}`} data-testid={`trip-card-${trip.id}`}>
      {compact && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="truncate text-xs font-bold text-slate-700" data-testid={`trip-name-${trip.id}`}>{trip.route_name || `${trip.origin_city} → ${trip.destination_city}`}</span>
          {trip.bus_number && !/assign/i.test(trip.bus_number) && <span className="chip shrink-0 bg-slate-100 text-slate-600">Bus {trip.bus_number}</span>}
        </div>
      )}
      <div className={`flex flex-col gap-4 ${compact ? "" : "sm:flex-row sm:items-center"}`}>
        <div className="flex flex-1 items-center gap-4">
          <div>
            <div className={`font-display font-extrabold text-slate-900 ${compact ? "text-xl" : "text-2xl"}`}>{fmtTime(trip.departure)}</div>
            <div className="text-xs font-semibold text-slate-500">{trip.origin_city}</div>
          </div>
          <div className="flex flex-1 flex-col items-center text-[11px] font-semibold text-slate-400">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{durationText(trip.duration_min)}</span>
            <span className="my-1 h-0.5 w-full rounded bg-gradient-to-r from-blue-200 via-[#0052FF] to-sky-300" />
            <span>{trip.distance_km} km</span>
          </div>
          <div className="text-right">
            <div className={`font-display font-extrabold text-slate-900 ${compact ? "text-xl" : "text-2xl"}`}>{addMinutes(trip.departure, trip.duration_min)}</div>
            <div className="text-xs font-semibold text-slate-500">{trip.destination_city}</div>
          </div>
        </div>
        <div className={`flex items-center justify-between gap-3 border-t border-slate-100 pt-3 ${compact ? "" : "sm:w-56 sm:flex-col sm:items-end sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0"}`}>
          <div className="sm:text-right">
            <div className={`font-display font-extrabold text-[#0052FF] ${compact ? "text-base" : "text-2xl"}`} data-testid={`trip-price-${trip.id}`}>{money(trip.fare)}</div>
            <div className={`text-xs font-bold ${low ? "text-rose-600" : "text-emerald-600"}`} data-testid={`trip-seats-${trip.id}`}>{trip.seats_available} of {trip.capacity} seats left</div>
          </div>
          <Button disabled={!trip.seats_available} onClick={() => onSelect(trip)} className="rounded-full bg-[#0052FF] px-5 font-bold hover:bg-[#0041CC] btn-press" data-testid={`select-trip-button-${trip.id}`}>
            {trip.seats_available ? <>Select seats <ArrowRight className="h-4 w-4" /></> : "Full"}
          </Button>
        </div>
      </div>
      <ViaLine trip={trip} />
      {!compact && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-dashed border-slate-200 pt-3">
          <span className="chip bg-blue-600 text-white">{trip.bus_type}</span>
          <span className="chip bg-slate-100 text-slate-600">{trip.capacity}-seater · {trip.bus_number}</span>
          <Amenities list={trip.amenities} />
        </div>
      )}
      {compact && <div className="mt-2 text-xs text-slate-500">{trip.bus_type} · {trip.bus_number} · Driver {trip.driver_name}</div>}
    </div>
  );
}
